import omit from 'lodash/omit'
import { logger } from 'v2/utils/logger'
import { useGenerateNodeMetadataMutation } from '../queries'
import type { ExistingNodeSummary, FlowNodeTypes, GenericNode } from '../types'
import { nodeNameSchema, refreshNodeVars, scheduleNodeVariableReferenceRewrite } from '../utils'
import { useTypedReactFlow } from './use-react-flow'

/**
 * Generate AI metadata for a node optimistically.
 *
 * Returns the applied metadata so callers (e.g. the "Use auto-name" button) can sync
 * open form state with it, or null when generation failed or the node disappeared.
 * When the node's name is user-pinned (data.name_source === 'user') the AI-generated
 * name is discarded and only the description is applied.
 */
export const useGenerateNodeMetadataOptimistically = () => {
  const generateNodeMetadataMutation = useGenerateNodeMetadataMutation()
  const {
    getNode,
    getNodes,
    getEdges,
    updateNodeData: reactFlowUpdateNodeData,
  } = useTypedReactFlow()

  return async (
    nodeId: string,
    nodeType: FlowNodeTypes,
    config: GenericNode['data']['config'],
    allNodes: GenericNode[],
    updateNodeData: (nodeId: string, data: Partial<GenericNode['data']>) => void,
    // overridePin: rename even a pinned node and clear the pin — the "Auto-name"
    // button's path. The pin is only cleared when a usable AI name was applied;
    // failures and rejected names leave the node pinned and unchanged.
    options?: { overridePin?: boolean },
  ): Promise<{ name: string; description?: string; aiNameApplied: boolean } | null> => {
    try {
      // Prepare existing nodes data for uniqueness checking
      const existingNodes: ExistingNodeSummary[] = allNodes
        .filter((n) => n.id !== nodeId && n.type !== 'menu') // Exclude current node and menu node
        .map((n) => ({
          name: n.data.config.name,
          config: n.data.config,
        }))

      // Send config without name and description
      const configForAI = omit(config, ['name', 'description'])

      // Generate AI metadata
      const aiGeneratedMetadata = await generateNodeMetadataMutation.mutateAsync({
        nodeId,
        nodeType,
        config: configForAI,
        existingNodes,
        existingName: config.name,
        existingDescription: config.description,
      })

      // Get the current node data to avoid overwriting user changes
      const currentNode = getNode(nodeId)
      if (!currentNode) {
        logger.warn('Node not found when updating AI metadata', { nodeId })
        return null
      }

      // Store the old name for reference updating
      const oldName = currentNode.data.config.name
      const isNamePinned = !options?.overridePin && currentNode.data.name_source === 'user'

      // The AI name is applied only if it passes the same rules typed names obey
      // (schema + uniqueness) — the endpoint's uniqueness retry is a soft guarantee,
      // and a collision would corrupt name-keyed {{Name.var}} references.
      const rawAiName = aiGeneratedMetadata.node_name?.trim()
      // Uniqueness is checked against a FRESH node snapshot — allNodes was captured
      // before the request and a rename can land while it is in flight.
      const isAiNameUsable =
        !!rawAiName &&
        nodeNameSchema.safeParse(rawAiName).success &&
        !getNodes().some(
          (n) =>
            n.id !== nodeId && n.data.config.name.trim().toLowerCase() === rawAiName.toLowerCase(),
        )
      const aiNameApplied = !isNamePinned && isAiNameUsable
      const newName = aiNameApplied ? (rawAiName as string) : currentNode.data.config.name
      const newDescription = aiGeneratedMetadata.node_description || undefined

      // Update the node metadata using current config to avoid race conditions.
      // On overridePin the pin is cleared here, atomically with the applied name —
      // a failed generation OR a rejected AI name leaves the node pinned.
      updateNodeData(nodeId, {
        config: {
          ...currentNode.data.config,
          name: newName,
          description: newDescription,
        },
        ...(options?.overridePin && aiNameApplied ? { name_source: undefined } : {}),
      })

      // Refresh node vars after AI metadata generation since the name might have changed
      setTimeout(() => {
        refreshNodeVars(nodeId)
      }, 0)

      // Update node variable references in downstream nodes when name changes
      scheduleNodeVariableReferenceRewrite(nodeId, oldName, newName, reactFlowUpdateNodeData, () =>
        getEdges(),
      )

      logger.info('AI node metadata generated successfully', {
        nodeId,
        nodeType,
        generatedName: aiGeneratedMetadata.node_name,
        isNamePinned,
      })
      return { name: newName, description: newDescription, aiNameApplied }
    } catch (error) {
      // Silently fail - the node will keep its current name as fallback
      logger.warn('AI node metadata generation failed, keeping current name', {
        nodeId,
        nodeType,
        currentName: config.name,
        error: error instanceof Error ? error.message : String(error),
      })
      return null
    }
  }
}
